// The product name as a link to the product site, for the "Powered by
// SupplierClear" credits. One component so the URL, the new-tab behaviour and
// the `rel` guard cannot drift between the places the credit is printed.
//
// What renders: an <a> whose visible text is PRODUCT_NAME, followed by a
// visually hidden " (opens in a new tab)" so screen readers announce the new
// tab before it happens. Colour and weight are inherited from the caller (each
// credit is styled to its own chrome). The link is deliberately NOT underlined
// (a product call: the credit should read as quiet chrome, not body copy); it
// dims slightly on hover for pointer feedback and keeps a visible focus ring in
// the current text colour for keyboard users. `no-underline` is explicit so no
// inherited or container rule can bring an underline back.
//
// `data-product-link` marks the anchor so containers that style their own
// links (SignInHelp) can exclude it, keeping the credit identical everywhere.
//
// rel is `noopener` only: that alone blocks window.opener. noreferrer is
// deliberately dropped — the strict-origin-when-cross-origin referrer policy
// sends only the origin, never paths (so never invite tokens), and it lets
// supplierclear.com attribute the traffic. That policy is the browser default,
// and the admin, portal and ops index.html files also pin it with
// <meta name="referrer">, so a changed default cannot leak a path.
//
// The sr-only suffix is `normal-case` because several credits sit in
// `uppercase` containers, which would otherwise make screen readers that honour
// text-transform read it as capitals.
import type { CSSProperties } from 'react';
import { PRODUCT_NAME, PRODUCT_URL } from './BrandMark.tsx';

/** Screen-reader-only suffix announcing that the credit opens a new tab. */
export const PRODUCT_LINK_NEW_TAB_TEXT = ' (opens in a new tab)';

export function ProductLink({
  className = '',
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <a
      href={PRODUCT_URL}
      target="_blank"
      rel="noopener"
      data-product-link=""
      className={`relative rounded-sm no-underline transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current ${className}`.trim()}
      style={style}
    >
      {PRODUCT_NAME}
      <span className="sr-only normal-case">{PRODUCT_LINK_NEW_TAB_TEXT}</span>
    </a>
  );
}
